import { ActivatedRouteSnapshot } from '@angular/router';

import { PAGE_ITERATION_ROUTE_ID } from './page-route-helper.class';
import { dependencies } from './workflow.dependency';
import { Workflow, WorkflowPage } from './workflow.type';
import { FlowInstance, FlowService } from './flow.service';
import { PageInstance, PageService } from './page.service';

interface ParsedPageUrl {
  flowId: string;
  pageId: string;
  pageIterationId?: number;
}

/**
 * A helper for a page URL. This class will provide helper methods to extract config element from a URL
 */
export class PageUrlHelper {
  constructor(
    private readonly flowService: FlowService,
    private readonly pageService: PageService,
    private readonly activatedRouteSnapshot: ActivatedRouteSnapshot,
    private readonly baseUrl: string = '/'
  ) {}

  /**
   * Get flow based on a path
   */
  public getFlow(path?: string): FlowInstance {
    const parsedUrl = this.parseUrl(path);
    if (parsedUrl?.flowId) {
      return this.flowService.createFlow(parsedUrl.flowId);
    }
  }

  /**
   * Get page based on a path
   */
  public getPageFlow(path?: string): PageInstance {
    const parsedUrl = this.parseUrl(path);
    if (parsedUrl?.pageId) {
      return this.pageService.createPage(parsedUrl.pageId, parsedUrl.flowId, parsedUrl.pageIterationId);
    }
  }

  /**
   * This function parses a url in location.href and extract workflow and page elements. Example:
   * url: mortgage/purchase/personal-info-co-applicant/0 ->
   *        {flowId: mortgage/purchase, pageId: personal-info-co-applicant-#, pageIterationId: 0 }
   *
   * url: mortgage/purchase/personal-info ->
   *        {flowId: mortgage/purchase, pageId: personal-info, pageIterationId: null }
   */
  private parseUrl(path?: string, iterationId?: number): ParsedPageUrl {
    const urlPath = path || this.locationPath;
    const flow = this.getFlowConfig(urlPath);
    if (flow) {
      const page = this.getPageConfig(flow, urlPath, iterationId);
      return {
        flowId: flow.id,
        pageId: page?.id,
        pageIterationId: iterationId ?? this.iterationId
      };
    }
  }

  /**
   * If HashLocationStrategy is enabled, convert hash based URL to a regular URL
   */
  private get hrefLocationPath(): string {
    const hashTagIdx = location.href.indexOf('/#/');
    if (hashTagIdx === -1) {
      const baseUrlIdx = location.pathname.indexOf(this.baseUrl) + this.baseUrl.length;
      return location.pathname.substr(baseUrlIdx);
    }

    return location.href.substr(hashTagIdx).replace('/#/', '');
  }

  /**
   * If HashLocationStrategy is enabled, convert hash based URL to a regular URL
   */
  private get locationPath(): string {
    const snapshot = this.findActivatedRouteSnapshot();
    if (snapshot?.url.length) {
      return snapshot.url.map((ur) => ur.path).join('/');
    }
    return this.hrefLocationPath;
  }

  /**
   * find workflow config associated to the URL path. for example;
   * In mortgage/purchase/personal-info, result will be workflow config for mortgage/purchase
   */
  private getFlowConfig(urlPath: string): Workflow {
    let sanitizedPath = urlPath;
    if (urlPath.startsWith('/')) {
      sanitizedPath = urlPath.substring(1);
    }
    if (this.workflowFromRouteData) {
      return this.workflowFromRouteData;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (const [flowId, flowConfig] of Object.entries(dependencies.config.flows)) {
      const flowPath = (flowConfig && this.getFlowPathWithPrefix(flowConfig)) || '';
      sanitizedPath = this.updatePathWithPrefix(sanitizedPath, flowConfig);
      if (flowPath + '/' === sanitizedPath.substr(0, flowPath.length + 1)) {
        return flowConfig;
      }
    }
  }

  private updatePathWithPrefix(path: string, flowConfig: Workflow): string {
    const prefix = flowConfig?.routePrefix ? flowConfig.routePrefix : '';
    return path?.includes(prefix) ? path : `${prefix}${path}`;
  }

  public getFlowPathWithPrefix(flowConfig: Workflow): string {
    const prefix = flowConfig.routePrefix ? flowConfig.routePrefix : '';
    const flowPath = `${prefix}${flowConfig.path}`;
    return flowPath;
  }

  /**
   * find workflow page config associated to the URL path. for example;
   * In mortgage/purchase/personal-info, result will be workflow page config for personal-info
   * In mortgage/purchase/personal-info-co-applicant/0, result will be workflow page config for personal-info-#
   * ToDo: Support for having iteration id in middle of the URL or at the beginning of the URL
   */
  private getPageConfig(flow: Workflow, urlPath, iterationId?: number): WorkflowPage {
    if (this.pageFlowFromRouteData) {
      return this.pageFlowFromRouteData;
    }

    const pathWithoutPrefix = urlPath.replace(`${flow.routePrefix}`, '');
    const pathWithoutFlowPath = pathWithoutPrefix.replace(`${flow.path}/`, '');
    const id = iterationId ?? this.iterationId;
    const pathWithIterationSymbol = id != null ? pathWithoutFlowPath.replace(`/${this.iterationId}`, `-#`) : pathWithoutFlowPath;

    for (const [pageId, pageConfig] of Object.entries(dependencies.config.pages)) {
      if (`${pageConfig.path}` === pathWithIterationSymbol && flow.pages.indexOf(pageId) > -1) {
        return pageConfig;
      }
    }
  }

  /**
   * return iteration id from active route
   */
  private get iterationId(): number {
    const id = this.findActivatedRouteSnapshot().paramMap.get(PAGE_ITERATION_ROUTE_ID);
    if (id !== null) {
      return +id;
    }
  }

  /**
   * Find ActivatedRouteSnapshot Route by looking at one level deep the route child
   * a better approach could be async way
   * https://medium.com/@eng.ohadb/how-to-get-route-path-parameters-in-an-angular-service-1965afe1470e
   */
  private findActivatedRouteSnapshot(): ActivatedRouteSnapshot {
    if (this.activatedRouteSnapshot.children.length === 0) {
      return this.activatedRouteSnapshot;
    }

    return this.activatedRouteSnapshot.children[0];
  }

  /**
   * If Workflow id is found in route data, use that Id to find the workflow
   */
  private get workflowFromRouteData(): Workflow {
    const routeData = this.findActivatedRouteSnapshot().data;
    const workFlowId = routeData?.workflowId;
    if (workFlowId) {
      return dependencies.config.flows[workFlowId];
    }
  }

  /**
   * If Page id is found in route data, use that Id to find the workflow
   */
  private get pageFlowFromRouteData(): WorkflowPage {
    const routeData = this.findActivatedRouteSnapshot().data;
    const pageId = routeData?.pageId;
    if (pageId) {
      return dependencies.config.pages[pageId];
    }
  }
}
